/**
 * Position Assignment Registry
 *
 * Drives the Custom Defense Builder's per-position dropdown: for each defensive position
 * (CB1, DB/LB1, FS, CB2, DB/LB2, SS, LB) this registry lists which coverage responsibilities
 * are selectable, and whether that position actually has real reaction behavior implemented
 * for that coverage yet.
 *
 * SCALABILITY: this is the single seam to update when new per-position behavior ships.
 *   - Building a new reaction for an existing coverage on a position that currently "holds"
 *     (e.g. DB/LB1 in Cover 4 Match)? Flip that option's `isImplemented` to `true`.
 *   - Adding a brand new coverage entirely? Add one option per position it applies to.
 * Every consumer (the builder page, the assignment dropdown, validation) reads from
 * `getAssignmentOptions()` / `POSITION_ASSIGNMENT_REGISTRY`, so nothing else needs to change.
 *
 * DESCRIPTIONS: the per-position prose shown in the builder is NOT stored here — it is pulled
 * from `src/utils/positionDescriptions.ts`, the same source the simulator uses on hover. Edit a
 * position's coverage description there and both the simulator and the builder update together.
 */

import {
  DefensePositionId,
  PositionAssignmentOption,
  CoverageType,
  FormationType,
  CustomDefenseAssignments,
  CustomDefenseClassification,
} from '../types';
import { getPositionDescription } from '../utils/positionDescriptions';

/**
 * All defensive position ids, in a stable display order (left side to right side,
 * matching how they're laid out on the field).
 */
export const DEFENSE_POSITION_IDS: DefensePositionId[] = ['cb1', 'dblb1', 'fs', 'ss', 'dblb2', 'cb2', 'lb'];

/** Human-readable labels for each defensive position. */
/**
 * User-facing position names.
 *
 * `dblb1` / `dblb2` were shown as "DB/LB1" and "DB/LB2". That is an accurate description of the
 * ROSTER ambiguity — the slot is a nickel in some schemes and a linebacker in others — but it is a
 * dev-side way of saying it, and no coach calls the position that. They call it the apex: the
 * defender apexed between #2 and the box. The coverage steering docs in this repo use "Apex"
 * throughout too ("Apex/Slot (DB/LB) — Play Wall on #2"), so this brings the UI in line with the
 * documentation.
 *
 * The ids stay `dblb1` / `dblb2`, so no saved defense, rule file or test changes.
 */
export const DEFENSE_POSITION_LABELS: Record<DefensePositionId, string> = {
  cb1: 'CB1',
  dblb1: 'Apex 1',
  fs: 'FS',
  ss: 'SS',
  dblb2: 'Apex 2',
  cb2: 'CB2',
  lb: 'LB',
};

/**
 * A coverage option's STRUCTURE only — which coverage, its display label (the dropdown name),
 * and whether the behavior is built. The human-readable per-position `description` is NOT stored
 * here: it is single-sourced from `src/utils/positionDescriptions.ts` (the same text the simulator
 * shows on hover) and attached in {@link withDescription}, so wording lives in exactly one place.
 */
type RegisteredAssignment = Omit<PositionAssignmentOption, 'description'>;

/** Man: fully implemented for every position (see src/rules/manRules.ts). */
const MAN_OPTION: RegisteredAssignment = { coverage: 'man', label: 'Man', isImplemented: true };

/**
 * Cover 4 Match: fully implemented for every position — CB1/CB2 (corners) and FS/SS (safeties)
 * run the perception-based man-match ("MOD") read, DB/LB1/DB/LB2 (apex/slot) run the underneath
 * "Wall-Read on #2" read, and LB runs the "Play RB" hook read. See src/rules/reactionEngine.ts.
 */
const COVER4_MATCH_OPTION: RegisteredAssignment = {
  coverage: 'cover4Match',
  // Matches the simulator's dropdown exactly. Was "Cover 4 Match (Quarter)" — singular "Quarter"
  // where coaches say "Quarters", and a different label from the same scheme's name elsewhere.
  label: 'Cover 4 Match',
  isImplemented: true,
};

/**
 * Cover 3 Match (doubles): fully implemented for every position on the reaction engine —
 * CB1/CB2 deep-third MOD man-match, DB/LB1/SS apex "match #2", FS single-high deep middle, and
 * LB/DB/LB2 the two inside hooks. See src/rules/reactionEngine.ts.
 */
const COVER3_MATCH_OPTION: RegisteredAssignment = {
  coverage: 'cover3Match',
  label: 'Cover 3 Match',
  isImplemented: true,
};

/**
 * Cover 6 (doubles + trips): fully implemented for every position on the reaction engine — Cover 4
 * on the LEFT (CB1/DB/LB1/FS), Cover 2 on the RIGHT (CB2/DB/LB2/SS), LB hook in the middle.
 * See src/rules/reactionEngine.ts.
 */
const COVER6_OPTION: RegisteredAssignment = {
  coverage: 'cover6',
  label: 'Cover 6',
  isImplemented: true,
};

/**
 * Cover 4 Palms (doubles + trips): fully implemented for every position on the reaction engine —
 * CB1/CB2 play the Palms corner (MOD man-match on #1 + jump #2's early "Out"), DB/LB1/DB/LB2 wall
 * #2, FS/SS are robber safeties, LB hammers #3. Trips simplifies the weak side (CB2 man, SS poach,
 * DB/LB2 weak apex). See src/rules/reactionEngine.ts.
 */
const COVER4_PALMS_OPTION: RegisteredAssignment = {
  coverage: 'cover4Palms',
  label: 'Cover 4 Palms',
  isImplemented: true,
};

/** Which coverage options are selectable for each defensive position (structure only). */
const ASSIGNMENT_STRUCTURE: Record<DefensePositionId, RegisteredAssignment[]> = {
  cb1: [MAN_OPTION, COVER4_MATCH_OPTION, COVER3_MATCH_OPTION, COVER6_OPTION, COVER4_PALMS_OPTION],
  cb2: [MAN_OPTION, COVER4_MATCH_OPTION, COVER3_MATCH_OPTION, COVER6_OPTION, COVER4_PALMS_OPTION],
  fs: [MAN_OPTION, COVER4_MATCH_OPTION, COVER3_MATCH_OPTION, COVER6_OPTION, COVER4_PALMS_OPTION],
  ss: [MAN_OPTION, COVER4_MATCH_OPTION, COVER3_MATCH_OPTION, COVER6_OPTION, COVER4_PALMS_OPTION],
  dblb1: [MAN_OPTION, COVER4_MATCH_OPTION, COVER3_MATCH_OPTION, COVER6_OPTION, COVER4_PALMS_OPTION],
  dblb2: [MAN_OPTION, COVER4_MATCH_OPTION, COVER3_MATCH_OPTION, COVER6_OPTION, COVER4_PALMS_OPTION],
  lb: [MAN_OPTION, COVER4_MATCH_OPTION, COVER3_MATCH_OPTION, COVER6_OPTION, COVER4_PALMS_OPTION],
};

/**
 * The builder is formation-agnostic (a saved defense applies to whatever formation is live in
 * the Simulator), so descriptions use the doubles reference from positionDescriptions.ts.
 */
const BUILDER_DESCRIPTION_FORMATION: FormationType = 'doubles';

/**
 * Attach the single-sourced `description` (from positionDescriptions.ts — the same text the
 * simulator tooltip uses) to a structural option. The dropdown keeps showing the coverage
 * `label`; only the description prose is shared, so it's edited in one place.
 */
function withDescription(
  position: DefensePositionId,
  registered: RegisteredAssignment
): PositionAssignmentOption {
  return {
    ...registered,
    description: getPositionDescription(position, registered.coverage, BUILDER_DESCRIPTION_FORMATION).description,
  };
}

/**
 * The registry: which coverage options are selectable for each defensive position, with each
 * option's description pulled from the shared position-descriptions source. Built once at load.
 */
export const POSITION_ASSIGNMENT_REGISTRY: Record<DefensePositionId, PositionAssignmentOption[]> =
  DEFENSE_POSITION_IDS.reduce((registry, position) => {
    registry[position] = ASSIGNMENT_STRUCTURE[position].map((option) => withDescription(position, option));
    return registry;
  }, {} as Record<DefensePositionId, PositionAssignmentOption[]>);

/** Every coverage option registered for a given defensive position. */
export function getAssignmentOptions(position: DefensePositionId): PositionAssignmentOption[] {
  return POSITION_ASSIGNMENT_REGISTRY[position] ?? [];
}

/** The specific option (label/implemented flag/description) for a position + coverage pair, if registered. */
export function getAssignmentOption(
  position: DefensePositionId,
  coverage: CoverageType
): PositionAssignmentOption | undefined {
  return getAssignmentOptions(position).find((option) => option.coverage === coverage);
}

/** Whether a coverage is selectable at all for a position (registered, regardless of implemented state). */
export function isAssignmentAvailable(position: DefensePositionId, coverage: CoverageType): boolean {
  return getAssignmentOption(position, coverage) !== undefined;
}

/** Whether a position's assignment to a coverage has real reaction behavior built (vs. a hold placeholder). */
export function isAssignmentImplemented(position: DefensePositionId, coverage: CoverageType): boolean {
  return getAssignmentOption(position, coverage)?.isImplemented ?? false;
}

/** The default (first registered) assignment for a position — used to seed a new custom defense. */
export function getDefaultAssignment(position: DefensePositionId): CoverageType {
  return getAssignmentOptions(position)[0]?.coverage ?? 'man';
}

/** A fresh set of default assignments for all 7 positions — used to seed a new custom defense. */
export function getDefaultAssignments(): Record<DefensePositionId, CoverageType> {
  const assignments = {} as Record<DefensePositionId, CoverageType>;
  for (const position of DEFENSE_POSITION_IDS) {
    assignments[position] = getDefaultAssignment(position);
  }
  return assignments;
}

/**
 * The single shared definition of complete vs. draft (Req 6.1, 6.2). Used by the Simulator
 * (CustomDefenseContext, to filter selectable options) and by the Defense Builder (to show a
 * draft badge) — never duplicated.
 *
 * Always computed fresh from the current DEFENSE_POSITION_IDS/isAssignmentImplemented — never
 * cached — so a registry update (e.g. flipping a placeholder to implemented) is reflected
 * immediately for existing saved assignments without any re-save (Req 6.6).
 */
export function classifyCustomDefense(assignments: CustomDefenseAssignments): CustomDefenseClassification {
  const allImplemented = DEFENSE_POSITION_IDS.every((position) =>
    isAssignmentImplemented(position, assignments[position])
  );
  return allImplemented ? 'complete' : 'draft';
}
